/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, int32, union, void as voidType } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";

export type ClaimableBalanceEntryExtensionV1ExtWire = { v: 0 };

export type ClaimableBalanceEntryExtensionV1ExtVariantName = "v0";

/**
 * ```xdr
 * union switch (int v)
 *     {
 *     case 0:
 *         void;
 *     }
 * ```
 */
abstract class ClaimableBalanceEntryExtensionV1ExtBase extends XdrValue {
  abstract readonly type: ClaimableBalanceEntryExtensionV1ExtVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === ClaimableBalanceEntryExtensionV1ExtBase) {
      throw new TypeError(
        "new xdr.ClaimableBalanceEntryExtensionV1Ext(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.ClaimableBalanceEntryExtensionV1Ext.v0() " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<ClaimableBalanceEntryExtensionV1ExtWire> =
    union("ClaimableBalanceEntryExtensionV1Ext", {
      switchOn: int32(),
      cases: [case_("v0", 0, voidType())],
      switchKey: "v",
    });

  static v0(): ClaimableBalanceEntryExtensionV1ExtV0 {
    return new ClaimableBalanceEntryExtensionV1ExtV0();
  }

  static fromXdrObject(
    wire: ClaimableBalanceEntryExtensionV1ExtWire,
  ): ClaimableBalanceEntryExtensionV1Ext {
    switch (wire.v) {
      case 0:
        return new ClaimableBalanceEntryExtensionV1ExtV0();
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `ClaimableBalanceEntryExtensionV1Ext: unknown v ${(wire as { v: unknown }).v}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete ClaimableBalanceEntryExtensionV1Ext variant.
   * Use this instead of `instanceof ClaimableBalanceEntryExtensionV1Ext`: the exported `ClaimableBalanceEntryExtensionV1Ext` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `ClaimableBalanceEntryExtensionV1Ext.is(x)` narrows to the union.
   */
  static is(value: unknown): value is ClaimableBalanceEntryExtensionV1Ext {
    return value instanceof ClaimableBalanceEntryExtensionV1ExtBase;
  }

  abstract toXdrObject(): ClaimableBalanceEntryExtensionV1ExtWire;
}

export class ClaimableBalanceEntryExtensionV1ExtV0 extends ClaimableBalanceEntryExtensionV1ExtBase {
  readonly type = "v0" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<ClaimableBalanceEntryExtensionV1ExtWire, { v: 0 }> {
    return { v: 0 };
  }
}

export type ClaimableBalanceEntryExtensionV1Ext =
  ClaimableBalanceEntryExtensionV1ExtV0;
export const ClaimableBalanceEntryExtensionV1Ext =
  ClaimableBalanceEntryExtensionV1ExtBase;
